import { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames/bind';
import { isEmpty } from 'lodash/fp';
import { LayoutDirection } from 'app/lib/constants';
import { useDispatch, useSelector } from 'app/lib/hooks';
import { Field, useFormContext } from 'app/components/core/Form';
import { ModalErrorBanner } from 'app/components/core/Modal';
import { Fieldset } from 'app/components/core/Fieldset';
import {
    DialogChapter,
    KeyValueSummary,
    SummaryHint
} from 'app/components/features/DialogWithChapters';
import { ComboBox } from 'app/components/core/ComboBox';
import {
    doListEnvironmentSchemas,
    doListEnvironmentDatabases,
    selectEnvironmentDatabases,
    selectIsLoadingEnvironmentDatabases,
    selectIsLoadingEnvironmentSchemas,
    selectEnvironmentSchemasForPropDatabase,
    selectEnvironmentDatabasesError
} from 'app/store/projectModels';

import * as styles from './EnvironmentDefaultsField.css';

const cx = classnames.bind(styles);

function useResetValues() {
    const dispatch = useDispatch();
    const { getFieldValue, setFieldValue, isPristine, pristine } = useFormContext();
    const serviceAccountUid = getFieldValue('account.serviceAccountUid');
    const user = getFieldValue('account.credentials.user');
    const role = getFieldValue('attributes.role.name');
    const haveRequiredFieldsChanged =
        !isPristine && (!role || pristine?.attributes?.role === false);

    useEffect(() => {
        if (haveRequiredFieldsChanged) {
            setFieldValue('defaults.database', null);
            setFieldValue('defaults.schema', null);
        }
    }, [setFieldValue, dispatch, haveRequiredFieldsChanged, serviceAccountUid, user, role]);
}

const renderItem = ({ name, value }) => (
    <span className={cx({ noValue: value === null })} key={name}>
        {name}
    </span>
);

export function EnvironmentDefaultsField() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getFieldValue } = useFormContext();
    const role = getFieldValue('attributes.role.name');
    const serviceAccountUid = getFieldValue('account.serviceAccountUid');
    const selectedDatabase = getFieldValue('defaults.database.value');
    const selectedSchema = getFieldValue('defaults.schema.value');
    const isLoadingDatabases = useSelector(selectIsLoadingEnvironmentDatabases);
    const isLoadingSchemas = useSelector(selectIsLoadingEnvironmentSchemas);
    const databases = useSelector(selectEnvironmentDatabases);
    const databasesError = useSelector(selectEnvironmentDatabasesError);
    const filteredSchemas = useSelector(selectEnvironmentSchemasForPropDatabase, {
        database: selectedDatabase
    });
    const isSnowflakeIdentityProvided = !isEmpty(serviceAccountUid);
    const isChapterDisabled = isEmpty(role) || !isSnowflakeIdentityProvided;

    useResetValues();

    const summary = useMemo(() => {
        const values = {
            [t('common:database')]: selectedDatabase,
            [t('glossary:schema')]: selectedSchema
        };

        return isChapterDisabled ? (
            <SummaryHint>{t('jobs:dialogs.environment.invalidForm')}</SummaryHint>
        ) : (
            <KeyValueSummary values={values} />
        );
    }, [t, isChapterDisabled, selectedDatabase, selectedSchema]);

    const handleOpenChapter = useCallback(() => {
        const params = { serviceAccountUid, role };
        dispatch(doListEnvironmentDatabases(params));
        dispatch(doListEnvironmentSchemas(params));
    }, [dispatch, role, serviceAccountUid]);

    const handleDatabaseChange = (value, { previousValue, setFieldValue }) => {
        if (previousValue?.name !== value?.name) {
            setFieldValue('defaults.schema', { name: t('common:none'), value: null });
        }
    };

    return (
        <DialogChapter
            label={t('jobs:dialogs.environment.envDefaults')}
            summary={summary}
            disabled={isChapterDisabled}
            onOpen={handleOpenChapter}
            isOptional>
            {databasesError && <ModalErrorBanner error={databasesError} />}
            <Fieldset direction={LayoutDirection.ROW}>
                <Field
                    component={ComboBox}
                    name="defaults.database"
                    label={t('common:database')}
                    placeholder={t('jobs:dialogs.environment.selectDatabase')}
                    options={[{ name: t('common:none'), value: null }, ...databases]}
                    isProcessing={isLoadingDatabases}
                    onChange={handleDatabaseChange}
                    itemRenderer={renderItem}
                />
                <Field
                    component={ComboBox}
                    name="defaults.schema"
                    placeholder={t('jobs:dialogs.environment.selectSchema')}
                    label={t('glossary:schema')}
                    options={[{ name: t('common:none'), value: null }, ...filteredSchemas]}
                    isProcessing={isLoadingSchemas}
                    itemRenderer={renderItem}
                />
            </Fieldset>
        </DialogChapter>
    );
}
